import { Flex } from "@mantine/core";
import { IconFileSpreadsheet, IconPlus } from "@tabler/icons-react";
import { productApi } from "apis/productApi";
import GroupButton, { ActionButton } from "components/ui/GroupButton/GroupButton";
import { SearchInput } from "components/ui/SearchInput/SearchInput";
import CustomText from "components/ui/CustomText/CustomText";
import { CommonParamsType } from "constants/types/common";
import { FORM_IDS } from "pages/Product/config";
import { FC, useRef, useState } from "react";
import { useSWRConfig } from "swr";
import { useAppModal } from "utils/hooks/ui/useAppModal";
import { useNotification } from "utils/hooks";
import { getProductListKey } from "utils/hooks/request/useProducts";
import CreateForm from "../CreateForm/CreateForm";

type ProductActionsProps = {
  handleSearch: (v: string) => void;
  params: CommonParamsType;
};

const ProductActions: FC<ProductActionsProps> = ({ handleSearch, params }) => {
  const { openCrudModal, closeCrudModal } = useAppModal();
  const activeModalId = useRef<string | null>(null);
  const { mutate } = useSWRConfig();
  const { notification } = useNotification();
  const [isExporting, setIsExporting] = useState(false);

  const handleCloseModal = () => {
    if (activeModalId.current) {
      closeCrudModal(activeModalId.current);
      activeModalId.current = null;
    }
  };

  const handleOpenCreateModal = () => {
    const modalId = openCrudModal(
      <CustomText variant="section-title">Tạo mới mặt hàng</CustomText>,
      {
        mode: "create",
        formId: FORM_IDS.CREATE,
        children: (
          <CreateForm
            mutate={() => mutate(getProductListKey(params))}
            onClose={handleCloseModal}
          />
        ),
        submitText: "Tạo mới",
      },
      750,
    );
    activeModalId.current = modalId;
  };

  const handleExportExcel = async () => {
    if (isExporting) return;
    setIsExporting(true);
    try {
      const exportParams = params.search ? { search: params.search } : undefined;
      const res = await productApi.export(exportParams);
      const url = URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.download = "mat-hang.xlsx";
      link.click();
      URL.revokeObjectURL(url);
      notification.success({ title: "Xuất Excel thành công" });
    } catch {
      notification.error({ title: "Xuất Excel thất bại" });
    } finally {
      setIsExporting(false);
    }
  };

  const actions: ActionButton[] = [
    {
      key: "export-excel",
      label: "Xuất Excel",
      icon: <IconFileSpreadsheet size={18} />,
      variant: "filled",
      color: "#217346",
      isLoading: isExporting,
      disabled: isExporting,
      onClick: handleExportExcel,
      permission: "product:export",
    },
    {
      key: "create",
      label: "Tạo mới",
      icon: <IconPlus size={18} />,
      variant: "filled",
      color: "blue",
      isLoading: false,
      onClick: handleOpenCreateModal,
      permission: "product:create",
    },
  ];

  return (
    <Flex justify="space-between" align="center">
      <SearchInput
        value={params.search || ""}
        onChange={handleSearch}
        placeholder="Tìm kiếm thông tin mặt hàng"
      />
      <GroupButton actions={actions} />
    </Flex>
  );
};

export default ProductActions;
